/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';

import type {
  AIMessageDraft,
  AutoSendCadence,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import ToggleSwitch from 'bundles/enterprise-ui/components/ToggleSwitch';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  draft: AIMessageDraft;
  updateDraft: (partial: Partial<AIMessageDraft>) => void;
  onDone: () => void;
};

const CADENCES: AutoSendCadence[] = ['7_days', '15_days', '30_days'];

const cadenceLabel = (cadence: AutoSendCadence): string => {
  switch (cadence) {
    case '7_days':
      return _t('Every 7 days');
    case '15_days':
      return _t('Every 15 days');
    case '30_days':
      return _t('Every 30 days');
    default:
      return _t('Every 15 days');
  }
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  supportText: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  autoSendRow: css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--cds-spacing-200);
    padding: var(--cds-spacing-200);
    background: var(--cds-color-interactive-background-primary-hover-weak);
    border-radius: var(--cds-spacing-100);
  `,
  autoSendCopy: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  cadencePills: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
    margin-top: var(--cds-spacing-100);
  `,
  /* Pill-shaped cadence toggle. Neutral light-grey border on every pill;
     selected state fills with `interactive/secondarySelected` (dark grey)
     and reverses the text color to white. */
  cadencePill: css`
    display: inline-flex;
    align-items: center;
    height: 36px;
    padding: 0 var(--cds-spacing-200);
    background: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 9999px;
    color: var(--cds-color-neutral-primary);
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background: var(--cds-color-neutral-disabled-weak);
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }
  `,
  cadencePillSelected: css`
    background: var(--cds-color-interactive-secondary-selected);
    color: var(--cds-color-neutral-background-primary);

    &:hover:not(:disabled) {
      background: var(--cds-color-interactive-secondary-hover);
    }
  `,
};

/**
 * Final confirmation screen. Confirms the send count and lets the admin
 * opt in to auto-sending the same message to new learners that match this
 * audience in the future, on a selectable cadence.
 */
const MessageSentScreen = ({ draft, updateDraft, onDone }: Props) => {
  return (
    <React.Fragment>
      <Dialog.Content>
        <div css={styles.body}>
          <Typography2 component="p" variant="bodyPrimary" css={styles.supportText}>
            <FormattedMessage
              message={_t('Your message has been sent to {count, plural, one {# learner} other {# learners}}.')}
              count={draft.audienceCount}
            />
          </Typography2>

          <div css={styles.autoSendRow}>
            <div css={styles.autoSendCopy}>
              <Typography2 component="p" variant="subtitleMedium">
                {_t('Auto-send to new matches')}
              </Typography2>
              <Typography2 component="p" variant="bodySecondary" css={styles.supportText}>
                {_t('New learners matching this audience will automatically receive this message.')}
              </Typography2>
              {draft.autoSendEnabled ? (
                <div css={styles.cadencePills} data-testid="ai-message-modal-cadence-row">
                  {CADENCES.map((cadence) => {
                    const selected = draft.autoSendCadence === cadence;
                    return (
                      <button
                        key={cadence}
                        type="button"
                        css={[styles.cadencePill, selected && styles.cadencePillSelected]}
                        onClick={() => updateDraft({ autoSendCadence: cadence })}
                        aria-pressed={selected}
                        data-testid={`ai-message-modal-cadence-${cadence}`}
                      >
                        {cadenceLabel(cadence)}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
            <ToggleSwitch
              aria-label={_t('Auto-send to new matches')}
              data-testid="ai-message-modal-auto-send-toggle"
              checked={draft.autoSendEnabled}
              onChange={(autoSendEnabled) => updateDraft({ autoSendEnabled })}
            />
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <Button variant="primary" onClick={onDone} data-testid="ai-message-modal-done">
          {_t('Done')}
        </Button>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default MessageSentScreen;
